import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { sortFieldLabel, type SortSpec } from "./filterTypes";
import { SortEditorPopover } from "./SortEditorPopover";

interface SortChipProps {
  sorts: ReadonlyArray<SortSpec>;
  onChange: (next: SortSpec[]) => void;
}

/** Single chip at the LEFT of the FilterChipsBar that summarises
 *  the current multi-sort state. Click body → SortEditorPopover.
 *  × → clear all sorts. Hidden when `sorts.length === 0` (parent
 *  handles the empty case). */
export function SortChip({ sorts, onChange }: SortChipProps) {
  const [open, setOpen] = useState(false);
  // Same mount-fade animation as F2 filter chips so sorts entering
  // the bar feel consistent with filters entering.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (sorts.length === 0) return null;

  const primary = sorts[0];
  const extras = sorts.length - 1;
  const PrimaryDirIcon = primary.dir === "asc" ? ArrowUp : ArrowDown;

  return (
    <div
      className={cn(
        "inline-flex items-center transition-all duration-200 motion-reduce:transition-none",
        mounted
          ? "translate-y-0 opacity-100"
          : "motion-safe:translate-y-1 motion-safe:opacity-0",
      )}
    >
      <Tooltip>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Edit sort"
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-l-full px-2.5 py-1 text-xs transition-colors",
                  "bg-slate-100 text-slate-700",
                  "dark:bg-slate-800 dark:text-slate-200",
                  "hover:bg-slate-200 dark:hover:bg-slate-700",
                )}
              >
                <ArrowUpDown
                  className="h-3.5 w-3.5 shrink-0 text-slate-500 dark:text-slate-400"
                  aria-hidden
                />
                <span className="text-slate-500 dark:text-slate-400">
                  Sorted by
                </span>
                <span
                  className="max-w-[180px] truncate font-medium text-slate-900 dark:text-slate-100"
                  title={sortFieldLabel(primary.field)}
                >
                  {sortFieldLabel(primary.field)}
                </span>
                <PrimaryDirIcon
                  className="h-3 w-3 shrink-0 text-slate-600 dark:text-slate-300"
                  aria-label={primary.dir === "asc" ? "ascending" : "descending"}
                />
                {extras > 0 && (
                  <span className="rounded-full bg-slate-200 px-1.5 py-0 text-[10px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    +{extras}
                  </span>
                )}
              </button>
            </TooltipTrigger>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-[420px] p-3"
            // F4-bis-bis: keep the popover open while the user
            // interacts with nested Radix portals (the field / dir
            // Selects inside SortEditorPopover render their
            // listboxes via SelectPrimitive.Portal → document.body,
            // which lives OUTSIDE this Popover's DOM subtree).
            // Without this guard, Radix's default
            // onInteractOutside treats clicks on Select options
            // as outside-clicks and closes the editor on every
            // option pick. Every Radix popper-based portal (Select,
            // DropdownMenu, Tooltip, …) marks its outer wrapper
            // with `data-radix-popper-content-wrapper`, so a single
            // closest() check covers all of them. Escape and true
            // outside-clicks (on the table / chips bar / page
            // background) still close the popover unchanged.
            onInteractOutside={(e) => {
              const target = e.target as HTMLElement | null;
              if (target?.closest("[data-radix-popper-content-wrapper]")) {
                e.preventDefault();
              }
            }}
          >
            <SortEditorPopover sorts={sorts} onChange={onChange} />
          </PopoverContent>
        </Popover>
        {!open && (
          <TooltipContent
            side="bottom"
            sideOffset={8}
            className="border border-slate-200 bg-white text-[11px] font-normal text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          >
            Click to edit · × to clear
          </TooltipContent>
        )}
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onChange([])}
            aria-label="Clear all sorts"
            className={cn(
              "inline-flex h-[26px] items-center rounded-r-full bg-slate-100 px-1.5 text-slate-400 transition-colors",
              "hover:bg-slate-300 hover:text-slate-800",
              "dark:bg-slate-800 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-100",
            )}
          >
            <X className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={8}
          className="border border-slate-200 bg-white text-[11px] font-normal text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          Clear sort
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
